import { isArray, isEmpty } from 'radash';
import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useShallow } from 'zustand/react/shallow';

import { HTMLInput } from '@/components/core/HTMLInput';

import useFocusOnAttribute from '../hooks/useFocusOnAttribute';
import { useIsAttributeReadonly } from '../hooks/useIsAttributeReadonly';
import { useIsHiddenAttribute } from '../hooks/useIsHiddenAttribute';
import { usePlaceholder } from '../hooks/usePlaceholder';
import { useValidationsStore,Validation } from '../hooks/useValidationsStore';
import { Layout } from '../Layout/Layout';
import { EditorComponent } from '../types';

interface HTMLComponentProps extends EditorComponent {
    // Integer specific props go here
    className?: string;
    isDimensional?: boolean;
}

const HTMLComponent = ({
    attribute,
    direction = 'horizontal',
    fieldPath = attribute.name,
    hasErrors: parentHasErrors = false,
    hasWarnings: parentHasWarnings = false,
    hideLabel,
    hidden = false,
    readonly = false,
    isKeyAttribute = false,
    isDimensional = false,
    beforeAttributeControl,
    className,
    onBlur: parentOnBlur
}: HTMLComponentProps) => {

    const [htmlFocus, setHtmlFocus] = useState<boolean>(false);

    useFocusOnAttribute<HTMLInputElement>(
        { name: fieldPath },
        setHtmlFocus
    );

    const placeholder = usePlaceholder(attribute, readonly);

    const validationErrors = useValidationsStore(useShallow(state => state.validationErrors[fieldPath] as Validation[]));
    const validationWarnings = useValidationsStore(useShallow(state => state.validationWarnings[fieldPath] as Validation[]));
    const hasErrors = parentHasErrors || !isEmpty(validationErrors);
    const hasWarnings = parentHasWarnings || !isEmpty(validationWarnings);

    hidden = useIsHiddenAttribute(attribute, attribute.name, hidden);

    readonly = useIsAttributeReadonly(attribute, attribute.name, readonly);
    const { control } = useFormContext();

    const getNumberOfRows = () => {
        const { renderer } = attribute.params || {};
        const rendererParams: { [key: string]: string | number }  = {};
        if (isArray(renderer)) {
            renderer.forEach(({ key, value }) => {
                rendererParams[key] = value;
            });
        }
        return rendererParams['rows'] as number|| 5;
    };

    if (hidden) {
        return null;
    }

    return (
        <Layout
            attribute={attribute}
            hideLabel={hideLabel}
            direction={direction}
            fieldPath={fieldPath}
            gridMode={false}
            isKeyAttribute={isKeyAttribute}
            beforeAttributeControl={beforeAttributeControl}
            isClearable={!readonly && !isDimensional}
            labelVerticalAlign="top"
        >
            <div className="tw-flex tw-flex-row">
                <Controller
                    control= {control}
                    name={fieldPath}
                    render={({ field }) => {
                        const handleBlur = () => {
                            field.onBlur();
                            if (parentOnBlur) {
                                parentOnBlur();
                            }
                        };
                        return (
                            <HTMLInput
                                {...field}
                                id={fieldPath}
                                htmlFocus={htmlFocus}
                                placeholder={placeholder}
                                readOnly={readonly}
                                rows={getNumberOfRows()}
                                onBlur={handleBlur}
                                wrapperClassName={`${hasErrors ? 'error-input' : hasWarnings ? 'warning-input' : ''} ${className}`}
                            />
                        );
                    }}
                />
            </div>
        </Layout>
    );
};

export { HTMLComponent };
